/**
 * ImageCard Component
 * Individual image card with cover photo selection and tag footer.
 */

import { useState } from 'react';
import { Check, X, Loader2, Sparkles, RefreshCw, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { getProxiedImageUrl } from '@/lib/image-proxy';
import { TagSelector } from '@/components/tag-suggestions/TagSelector';
import type { LocationImage, TagRunUsage, TagSuggestion } from '@/types/api';
import { formatTagRunDetail, formatTagRunShort } from '@/lib/ai-run-usage';

interface ConfirmedTagInfo {
  tagId: number;
  tagName: string;
  isAiSuggested: boolean;
}

interface ImageCardProps {
  /** The image data */
  image: LocationImage;
  /** Array index of this image */
  index: number;
  /** Whether this image is selected as cover */
  isSelected: boolean;
  /** Whether this is the default cover (first image, no explicit selection) */
  isDefaultCover: boolean;
  /** Confirmed tags for this image */
  confirmedTags: ConfirmedTagInfo[];
  /** Pending tag suggestions for this image */
  pendingTags: TagSuggestion[];
  /** IDs of tags currently being processed */
  processingTagIds: Set<number>;
  /** IDs of tags currently being removed (Phase 26) */
  removingTagIds?: Set<number>;
  /** IDs of rejected tags */
  rejectedTagIds: number[];
  /** Handler for selecting as cover */
  onSelectCover: () => void;
  /** Handler for accepting a tag */
  onAcceptTag: (tagId: number) => void;
  /** Handler for rejecting a tag */
  onRejectTag: (tagId: number) => void;
  /** Handler for removing a confirmed tag (Phase 26) */
  onRemoveTag?: (tagId: number) => void;
  /** Handler for viewing image full-size */
  onImageView?: () => void;
  /**
   * Whether interactions are disabled: cover selection, the viewer, tag
   * accept/reject/add/remove, re-analyze and remove. Also set while a removal
   * is in flight, because every one of those addresses the image by its index
   * and the removal's refetch shifts the indexes.
   */
  disabled?: boolean;
  /** Location ID for manual tag addition (Phase 26) */
  locationId?: string;
  /** All tag IDs to exclude from manual tag selector (Phase 26) */
  excludeTagIds?: number[];
  /** Callback when a tag is manually added (Phase 26) */
  onTagAdded?: () => void;
  /** Handler for re-analyzing this single image */
  onReanalyzeImage?: () => void;
  /** Whether this image is currently being re-analyzed */
  isReanalyzingImage?: boolean;
  /**
   * Handler for removing this image from the listing, called with the URL of
   * the image the confirm dialog was opened for. Absent = the control is not offered.
   */
  onRemoveImage?: (imageUrl: string) => void;
  /** Whether this image's removal is in flight. */
  isRemovingImage?: boolean;
  /** When set, the remove button is disabled and this is its title (why). */
  removeDisabledReason?: string;
  /** What this photo's latest tag run cost (FR-142); absent when it has none. */
  tagRun?: TagRunUsage;
  /**
   * Multi-photo removal: while set, the card shows a checkbox, a click on the
   * image toggles it instead of opening the viewer, and the cover and single
   * remove controls are hidden.
   */
  selectionMode?: boolean;
  /** Whether this image is checked for removal (selection mode only). */
  isChecked?: boolean;
  /** Toggles this image's checkbox (selection mode only). */
  onToggleChecked?: () => void;
}

/**
 * ConfirmedTagBadge - displays a confirmed tag with optional AI indicator and remove button
 * Phase 26: Updated to support removal of confirmed tags
 */
function ConfirmedTagBadge({
  tag,
  isRemoving,
  disabled = false,
  onRemove,
}: {
  tag: ConfirmedTagInfo;
  isRemoving?: boolean;
  disabled?: boolean;
  onRemove?: () => void;
}): JSX.Element {
  // AI-confirmed tags are green, manual tags are blue
  const colorClasses = tag.isAiSuggested
    ? 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100'
    : 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100';

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 h-6 px-2 rounded-full text-xs font-medium',
        colorClasses,
        isRemoving && 'opacity-50',
        onRemove && 'pr-1' // Less padding on right when remove button is present
      )}
      data-testid={`confirmed-tag-${String(tag.tagId)}`}
    >
      {tag.isAiSuggested && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Sparkles className="h-3 w-3 text-amber-500 flex-shrink-0" />
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            AI suggested
          </TooltipContent>
        </Tooltip>
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="truncate max-w-[100px]">{tag.tagName}</span>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs max-w-[200px]">
          {tag.tagName}
        </TooltipContent>
      </Tooltip>
      {onRemove && (
        <button
          type="button"
          disabled={(isRemoving ?? false) || disabled}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="p-0.5 ml-0.5 rounded-full opacity-60 hover:opacity-100 hover:bg-red-100 text-current hover:text-red-600 transition-colors"
          aria-label={`Remove ${tag.tagName}`}
          data-testid={`remove-image-tag-${String(tag.tagId)}`}
        >
          {isRemoving ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <X className="h-3 w-3" />
          )}
        </button>
      )}
    </span>
  );
}

/**
 * PendingTagBadge - displays a pending tag with accept/reject buttons
 */
function PendingTagBadge({
  tag,
  isProcessing,
  disabled = false,
  onAccept,
  onReject,
}: {
  tag: TagSuggestion;
  isProcessing: boolean;
  disabled?: boolean;
  onAccept: () => void;
  onReject: () => void;
}): JSX.Element {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 h-6 pl-2 pr-1 rounded-full text-xs font-medium',
        'bg-amber-50 border border-amber-200 text-amber-700',
        isProcessing && 'opacity-60'
      )}
      data-testid={`pending-tag-${String(tag.tagId)}`}
    >
      <Sparkles className="h-3 w-3 text-amber-500 flex-shrink-0" />
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="truncate max-w-[80px]">{tag.tagName}</span>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs max-w-[200px]">
          {tag.tagName}
        </TooltipContent>
      </Tooltip>
      {isProcessing ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin ml-1" />
      ) : (
        <span className="inline-flex items-center ml-1">
          <button
            type="button"
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              onAccept();
            }}
            className="p-0.5 rounded-full hover:bg-green-100 text-green-600 hover:text-green-700 transition-colors"
            aria-label={`Accept ${tag.tagName}`}
            data-testid={`accept-tag-${String(tag.tagId)}`}
          >
            <Check className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              onReject();
            }}
            className="p-0.5 rounded-full hover:bg-red-100 text-red-500 hover:text-red-600 transition-colors"
            aria-label={`Reject ${tag.tagName}`}
            data-testid={`reject-tag-${String(tag.tagId)}`}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </span>
      )}
    </span>
  );
}

/**
 * ImageCard renders a single image with:
 * - Cover photo selection (click to view, hover button to select)
 * - Cover badge when selected
 * - Image number indicator
 * - Tag footer with confirmed and pending tags
 */
export function ImageCard({
  image,
  index,
  isSelected,
  isDefaultCover,
  confirmedTags,
  pendingTags,
  processingTagIds,
  removingTagIds,
  rejectedTagIds,
  onSelectCover,
  onAcceptTag,
  onRejectTag,
  onRemoveTag,
  onImageView,
  disabled = false,
  locationId,
  excludeTagIds = [],
  onTagAdded,
  onReanalyzeImage,
  isReanalyzingImage = false,
  onRemoveImage,
  isRemovingImage = false,
  removeDisabledReason,
  tagRun,
  selectionMode = false,
  isChecked = false,
  onToggleChecked,
}: ImageCardProps): JSX.Element {
  // The URL of the image the confirm dialog was opened for, captured when it
  // opens: a refetch while the dialog is open can move a different image onto
  // this card's index, and the removal must still address the one confirmed.
  const [pendingRemovalUrl, setPendingRemovalUrl] = useState<string | null>(null);
  const imageNumber = String(index + 1);

  // Filter out rejected tags from pending
  const filteredPendingTags = pendingTags.filter(
    (tag) => !rejectedTagIds.includes(tag.tagId)
  );

  const hasTags = confirmedTags.length > 0 || filteredPendingTags.length > 0;

  return (
    <div
      className={cn(
        'rounded-lg border overflow-hidden bg-card transition-all duration-100',
        disabled && 'opacity-50 cursor-not-allowed',
        isSelected && !selectionMode && 'ring-2 ring-primary ring-offset-2',
        selectionMode && isChecked && 'ring-2 ring-destructive ring-offset-2',
        !isSelected && !disabled && 'hover:shadow-md'
      )}
      data-testid={`unified-image-card-${imageNumber}`}
    >
      {/* Image section */}
      <div
        id={`unified-image-${imageNumber}`}
        role="option"
        aria-selected={isSelected}
        aria-label={`Image ${imageNumber}${isSelected ? ', selected as cover' : ''}${isDefaultCover ? ', default cover' : ''}`}
        tabIndex={isSelected ? 0 : -1}
        className="group relative aspect-[3/2] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
      >
        {/* Clickable image area */}
        <button
          type="button"
          onClick={() => {
            if (selectionMode) {
              onToggleChecked?.();
            } else if (onImageView) {
              onImageView();
            } else {
              onSelectCover();
            }
          }}
          disabled={disabled}
          className="w-full h-full cursor-pointer hover:scale-[1.02] transition-transform"
          tabIndex={-1}
          aria-label={
            selectionMode
              ? `${isChecked ? 'Unselect' : 'Select'} image ${imageNumber} for removal`
              : onImageView ? `View image ${imageNumber} full size` : `Select image ${imageNumber} as cover`
          }
        >
          {/* Uses 'preview' preset (400px) for grid thumbnails (FR-127) */}
        <img
            src={getProxiedImageUrl(image.url, 'preview')}
            alt={image.caption ?? `Partner image ${imageNumber}`}
            className="w-full h-full object-cover"
            loading="lazy"
            data-testid={`image-card-${String(index)}`}
          />
        </button>

        {/* Cover badge - top left */}
        {isSelected && (
          <div className="absolute inset-0 bg-primary/10 pointer-events-none">
            <Badge
              variant="default"
              className="absolute top-2 left-2 gap-1"
            >
              <Check className="h-3 w-3" />
              Cover
            </Badge>
          </div>
        )}

        {/* Default cover indicator */}
        {isDefaultCover && !isSelected && (
          <Badge
            variant="secondary"
            className="absolute top-2 left-2 opacity-80 pointer-events-none"
          >
            Default
          </Badge>
        )}

        {/* Re-analyzing overlay - dims image and shows spinner */}
        {isReanalyzingImage && (
          <div
            className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center pointer-events-none z-20"
            role="status"
            aria-live="polite"
            data-testid={`reanalyze-image-${imageNumber}-overlay`}
          >
            <Loader2 className="h-8 w-8 motion-safe:animate-spin text-white" aria-hidden="true" />
            <span className="text-white text-sm mt-2 font-medium">Analyzing photo {imageNumber}…</span>
          </div>
        )}

        {/* Image number - top right */}
        <span className="absolute top-2 right-2 text-xs bg-black/60 text-white px-1.5 py-0.5 rounded pointer-events-none">
          {imageNumber}
        </span>

        {/* "Set as Cover" button - top left, on hover */}
        {!isSelected && !disabled && !selectionMode && onImageView && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onSelectCover();
            }}
            className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity text-xs px-2 py-1 h-auto z-10"
            tabIndex={-1}
          >
            Set as Cover
          </Button>
        )}

        {/* Re-analyze image button - bottom right, on hover */}
        {onReanalyzeImage && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onReanalyzeImage();
                }}
                disabled={disabled || isReanalyzingImage}
                className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity h-7 w-7 p-0 z-10"
                tabIndex={-1}
                data-testid={`reanalyze-image-${imageNumber}-btn`}
              >
                {isReanalyzingImage ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Re-analyze this image</TooltipContent>
          </Tooltip>
        )}

        {/* Remove image button - bottom left, on hover. Rendered while the card is
            disabled too, so the removing card keeps its spinner and the others their reason. */}
        {onRemoveImage && !selectionMode && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="absolute bottom-2 left-2 z-10">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPendingRemovalUrl(image.url);
                  }}
                  disabled={disabled || isRemovingImage || removeDisabledReason !== undefined}
                  title={removeDisabledReason}
                  className="opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity h-7 w-7 p-0 text-destructive"
                  tabIndex={-1}
                  aria-label={`Remove image ${imageNumber}`}
                  data-testid={`remove-image-${imageNumber}-btn`}
                >
                  {isRemovingImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>{removeDisabledReason ?? 'Remove this image'}</TooltipContent>
          </Tooltip>
        )}

        {/* Removal checkbox - bottom left, where the single remove sits outside selection mode */}
        {selectionMode && (
          <span className="absolute bottom-2 left-2 z-10 flex h-7 w-7 items-center justify-center rounded bg-background/90 shadow">
            <Checkbox
              checked={isChecked}
              onCheckedChange={() => { onToggleChecked?.(); }}
              onKeyDown={(e) => {
                // Space toggles the checkbox itself; the grid's per-card handler would toggle it a
                // second time. Arrow keys still reach the grid's navigation.
                if (e.key === ' ' || e.key === 'Enter') e.stopPropagation();
              }}
              disabled={disabled}
              className="h-5 w-5"
              aria-label={`Select image ${imageNumber} for removal`}
              data-testid={`select-image-${imageNumber}-checkbox`}
            />
          </span>
        )}
      </div>

      {/* Tag footer - below image (always show if locationId provided for adding tags) */}
      {(hasTags || locationId !== undefined || tagRun !== undefined) && (
        <div className="p-2 border-t bg-muted/30 space-y-2">
          <div className="flex flex-wrap gap-1.5 items-center">
            {/* Confirmed tags */}
            {confirmedTags.map((tag) => (
              <ConfirmedTagBadge
                key={`confirmed-${String(tag.tagId)}`}
                tag={tag}
                isRemoving={removingTagIds?.has(tag.tagId)}
                disabled={disabled}
                onRemove={onRemoveTag ? () => { onRemoveTag(tag.tagId); } : undefined}
              />
            ))}

            {/* Pending tags */}
            {filteredPendingTags.map((tag) => (
              <PendingTagBadge
                key={`pending-${String(tag.tagId)}`}
                tag={tag}
                isProcessing={processingTagIds.has(tag.tagId)}
                disabled={disabled}
                onAccept={() => { onAcceptTag(tag.tagId); }}
                onReject={() => { onRejectTag(tag.tagId); }}
              />
            ))}
          </div>

          {/* What this photo's tag run cost (FR-142). A focusable tooltip trigger, so the model and
              full usage reach keyboard and touch users too; screen readers get them as text. */}
          {tagRun && !isReanalyzingImage && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="block text-left text-xs leading-tight text-muted-foreground underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none"
                  onClick={(e) => { e.stopPropagation(); }}
                  data-testid={`image-${imageNumber}-run-usage`}
                >
                  {formatTagRunShort(tagRun)}
                  <span className="sr-only">, {formatTagRunDetail(tagRun)}</span>
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-xs" data-testid={`image-${imageNumber}-run-detail`}>
                {formatTagRunDetail(tagRun)}
              </TooltipContent>
            </Tooltip>
          )}

          {/* Add tag selector (Phase 26) */}
          {locationId && (
            <div onClick={(e: React.MouseEvent) => { e.stopPropagation(); }}>
              <TagSelector
                locationId={locationId}
                scope="image"
                imageIndex={index}
                excludeTagIds={excludeTagIds}
                placeholder="Add tag..."
                testId={`image-tag-selector-${imageNumber}`}
                onTagAdded={onTagAdded}
                disabled={disabled}
              />
            </div>
          )}
        </div>
      )}

      {onRemoveImage && (
        <AlertDialog
          open={pendingRemovalUrl !== null}
          onOpenChange={(open) => { if (!open) setPendingRemovalUrl(null); }}
        >
          <AlertDialogContent
            data-testid="remove-image-dialog"
            onKeyDown={(e) => {
              // The dialog is portalled, but React events still bubble through the component
              // tree to the grid's per-card handler, where Enter sets the cover and Space opens the viewer.
              e.stopPropagation();
            }}
          >
            <AlertDialogHeader>
              <AlertDialogTitle>Remove image {imageNumber}?</AlertDialogTitle>
              <AlertDialogDescription>
                The photo is removed from this listing in the Bridge and, if the listing is published, from ScoutAround.
                Uploading the file again will not bring it back.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="remove-image-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                data-testid="remove-image-confirm"
                disabled={isRemovingImage}
                onClick={() => {
                  const url = pendingRemovalUrl;
                  setPendingRemovalUrl(null);
                  if (url !== null) onRemoveImage(url);
                }}
              >
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
